/* spx-carat-slider.js — dual-handle carat range control for the catalog filter drawer.
 *
 * Opt-in per template: only runs when #sp-catalog-grid carries data-sp-carat-slider.
 * (Grid section setting "carat_slider". Off everywhere else, so the engagement-ring
 * catalogs are untouched.)
 *
 * WHY INDEX-BASED, NOT VALUE-BASED:
 * carat here is a small set of manufactured sizes with badly non-linear spacing —
 * 0.5, 1, 1.2, 1.25, 1.5, 2, 3, 4, 5, 6, 8, 10. On a linear 0.5–10 track, half the
 * stops (and most of the inventory) would be crushed into the first ~17% and be
 * unhittable on a phone. So the handles move over the INDEX of the sorted stops:
 * every real size gets equal track space, and the slider can only ever land on a
 * size that exists. Native <input type=range step=1> gives snapping, keyboard
 * support and touch targets for free.
 *
 * It drives the same filter pipeline the checkboxes use (one render, not N fake
 * change events) via window.spxCatalog.setFacetValues, and leaves the original
 * checkbox list in the DOM — visually hidden — so no-JS still filters and the
 * existing drawer/rail/URL sync keeps working untouched.
 */
(function () {
  'use strict';

  var FACET = 'filter.v.m.custom.carat_weight';

  function stopsFrom(inputs) {
    return inputs.map(function (input) {
      var label = input.closest('label');
      var text = '';
      if (label) {
        var spans = label.querySelectorAll('span');
        if (spans.length) text = (spans[0].textContent || '').trim();
      }
      var countEl = label && label.querySelector('.sp-drawer__check-count');
      return {
        value: input.value,
        num: parseFloat(input.value),
        label: text || (input.value + ' ct'),
        count: countEl ? (countEl.textContent || '').trim() : '',
        input: input
      };
    }).filter(function (s) {
      return !isNaN(s.num);
    }).sort(function (a, b) {
      return a.num - b.num;
    });
  }

  function build(list, stops) {
    var last = stops.length - 1;
    var wrap = document.createElement('div');
    wrap.className = 'spx-carat';

    var readout = document.createElement('p');
    readout.className = 'spx-carat__readout';
    wrap.appendChild(readout);

    var track = document.createElement('div');
    track.className = 'spx-carat__track';
    var fill = document.createElement('span');
    fill.className = 'spx-carat__fill';
    track.appendChild(fill);

    function mkRange(cls, value, label) {
      var r = document.createElement('input');
      r.type = 'range';
      r.min = '0';
      r.max = String(last);
      r.step = '1';
      r.value = String(value);
      r.className = 'spx-carat__range ' + cls;
      r.setAttribute('aria-label', label);
      track.appendChild(r);
      return r;
    }
    var lo = mkRange('spx-carat__range--lo', 0, 'Minimum carat weight');
    var hi = mkRange('spx-carat__range--hi', last, 'Maximum carat weight');
    wrap.appendChild(track);

    var ends = document.createElement('p');
    ends.className = 'spx-carat__ends';
    ends.innerHTML = '<span>' + stops[0].label + '</span><span>' + stops[last].label + '</span>';
    wrap.appendChild(ends);

    function paint() {
      var a = Math.min(+lo.value, +hi.value);
      var b = Math.max(+lo.value, +hi.value);
      var pct = function (i) { return last === 0 ? 0 : (i / last) * 100; };
      fill.style.left = pct(a) + '%';
      fill.style.right = (100 - pct(b)) + '%';
      var total = 0;
      for (var i = a; i <= b; i++) {
        var n = parseInt(stops[i].count, 10);
        if (!isNaN(n)) total += n;
      }
      readout.textContent = (a === b)
        ? stops[a].label
        : stops[a].label + ' – ' + stops[b].label;
      if (a === 0 && b === last) readout.textContent = 'All sizes';
      readout.setAttribute('data-sp-carat-span', total ? String(total) : '');
    }

    function apply() {
      var a = Math.min(+lo.value, +hi.value);
      var b = Math.max(+lo.value, +hi.value);
      var vals = [];
      /* full span = no carat filter at all, rather than listing every value */
      if (!(a === 0 && b === last)) {
        for (var i = a; i <= b; i++) vals.push(stops[i].value);
      }
      if (window.spxCatalog && window.spxCatalog.setFacetValues) {
        window.spxCatalog.setFacetValues(FACET, vals);
      }
    }

    /* keep the two handles from crossing over each other */
    lo.addEventListener('input', function () {
      if (+lo.value > +hi.value) hi.value = lo.value;
      paint();
    });
    hi.addEventListener('input', function () {
      if (+hi.value < +lo.value) lo.value = hi.value;
      paint();
    });
    lo.addEventListener('change', apply);
    hi.addEventListener('change', apply);

    /* reflect whatever is already selected (deep link, back button, drawer sync) */
    var selected = [];
    for (var i = 0; i < stops.length; i++) if (stops[i].input.checked) selected.push(i);
    if (selected.length) {
      lo.value = String(selected[0]);
      hi.value = String(selected[selected.length - 1]);
    }
    paint();

    list.classList.add('sp-drawer__list--replaced');
    list.parentNode.insertBefore(wrap, list);
  }

  function scan() {
    var root = document.querySelector('#sp-catalog-grid[data-sp-carat-slider]');
    if (!root) return;
    var inputs = [].slice.call(document.querySelectorAll('input[name="' + FACET + '"]'));
    if (inputs.length < 3) return;               /* too few stops to be worth a slider */
    var list = inputs[0].closest('.sp-drawer__list');
    if (!list || list.classList.contains('sp-drawer__list--replaced')) return;
    var stops = stopsFrom(inputs);
    if (stops.length < 3) return;
    build(list, stops);
  }

  /* The catalog swaps its own HTML on every filter change, which destroys the slider,
     so rebuild whenever the drawer markup reappears. Debounced to one pass per frame. */
  var queued = null;
  function schedule() {
    if (queued) return;
    /* setTimeout, NOT requestAnimationFrame: rAF is suspended while the tab is in the
       background, so a drawer opened in a backgrounded tab would never get its slider. */
    queued = window.setTimeout(function () { queued = null; scan(); }, 0);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }
  new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true });
})();
